const ACCESS_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

export interface SessionTokens {
	token?: string | null;
	refresh_token?: string | null;
}

export const saveSession = ({ token, refresh_token }: SessionTokens) => {
	if (token) localStorage.setItem(ACCESS_TOKEN_KEY, token);
	if (refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
};

export const clearSession = () => {
	localStorage.removeItem(ACCESS_TOKEN_KEY);
	localStorage.removeItem(REFRESH_TOKEN_KEY);
};

export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export type RefreshOutcome = 'refreshed' | 'rejected' | 'unavailable';

const REFRESH_TIMEOUT_MS = 15000;
// How long a tab whose refresh was refused waits for a tab that won the same rotation.
const OTHER_TAB_WAIT_MS = 3000;
const OTHER_TAB_POLL_MS = 100;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const otherTabRefreshed = async (sent: string): Promise<boolean> => {
	for (
		let waited = 0;
		waited <= OTHER_TAB_WAIT_MS;
		waited += OTHER_TAB_POLL_MS
	) {
		const current = getRefreshToken();
		if (current === null) return false;
		if (current !== sent) return true;
		await sleep(OTHER_TAB_POLL_MS);
	}
	return false;
};

const requestRefresh = async (apiUrl: string): Promise<RefreshOutcome> => {
	const sent = getRefreshToken();
	if (!sent) return 'rejected';
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
	try {
		const response = await fetch(`${apiUrl}/auth/refresh`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ refresh_token: sent }),
			signal: controller.signal,
		});
		if (response.ok) {
			const body: SessionTokens = await response.json();
			if (!body.token || !body.refresh_token) return 'unavailable';
			const current = getRefreshToken();
			// A logout while this was in flight wins.
			if (current === null) return 'rejected';
			// Another tab already saved a newer token; keep it.
			if (current === sent) saveSession(body);
			return 'refreshed';
		}
		// Only 400/401 mean the token was refused; 403, 429 and 5xx are temporary.
		if (response.status !== 400 && response.status !== 401)
			return 'unavailable';
	} catch {
		return 'unavailable';
	} finally {
		clearTimeout(timeout);
	}
	return (await otherTabRefreshed(sent)) ? 'refreshed' : 'rejected';
};

let refreshing: Promise<RefreshOutcome> | null = null;

/** Get a new access token. Calls that arrive while one is running share it. */
export const refreshSession = (apiUrl: string): Promise<RefreshOutcome> => {
	refreshing ??= requestRefresh(apiUrl).finally(() => {
		refreshing = null;
	});
	return refreshing;
};
